import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Button, Modal, Form, Spinner, Alert, Table } from 'react-bootstrap';
import Navbarpro from './sidebar';
import { api } from '../../../config';

function PendingApprovals() {
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState('');
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        fetchPendingVendors();
    }, []);

    const fetchPendingVendors = async () => {
        setLoading(true);
        try {
            let data;
            try {
                const res = await api.get('/admin/vendors/pending');
                data = res.data || [];
            } catch {
                const res = await api.get('/api/vendor');
                data = res.data || [];
            }
            setVendors(data);
        } catch (err) {
            console.error('Error:', err);
            setError('Failed to load pending vendors');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (vendorId) => {
        setActionLoading(true);
        try {
            await api.post(`/postdatabase/${vendorId}`);
            setSuccess('Vendor approved successfully!');
            setVendors(prev => prev.filter(v => v._id !== vendorId));
            setShowModal(false);
        } catch (err) {
            setError('Failed to approve vendor');
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async () => {
        if (!selectedVendor) return;
        setActionLoading(true);
        try {
            // Remove from pending list (reject endpoint may not exist)
            setVendors(prev => prev.filter(v => v._id !== selectedVendor._id));
            setSuccess('Vendor application rejected');
            setShowRejectModal(false);
        } catch (err) {
            setError('Failed to reject vendor');
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="loading-state">
                <Navbarpro />
                <div className="text-center" style={{ marginTop: '20vh' }}>
                    <Spinner animation="border" />
                    <p className="mt-3">Loading pending approvals...</p>
                </div>
            </div>
        );
    }

    return (
        <div>
            <Navbarpro />

            <Container fluid className="py-4 px-4">
                {/* Page Header */}
                <div className="page-header d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h4 className="mb-1">
                            <i className="bi bi-hourglass-split me-2" style={{ color: 'var(--accent-yellow)' }}></i>
                            Pending Approvals
                        </h4>
                        <p className="text-secondary mb-0">Review and approve vendor registration requests</p>
                    </div>
                    <Button variant="outline-secondary" size="sm" onClick={fetchPendingVendors}>
                        <i className="bi bi-arrow-clockwise me-1"></i> Refresh
                    </Button>
                </div>

                {/* Alerts */}
                {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
                {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

                {/* Stats */}
                <Row className="g-3 mb-4">
                    <Col md={4}>
                        <div className="stat-card">
                            <div className="d-flex align-items-center gap-3">
                                <div className="icon" style={{ background: 'rgba(255, 214, 0, 0.1)', color: 'var(--accent-yellow)' }}>
                                    <i className="bi bi-hourglass"></i>
                                </div>
                                <div>
                                    <div className="value">{vendors.length}</div>
                                    <div className="label">Pending Requests</div>
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Vendor Cards Grid */}
                {vendors.length === 0 ? (
                    <div className="empty-state">
                        <i className="bi bi-inbox"></i>
                        <h5>No pending approvals</h5>
                        <p>All vendor requests have been processed.</p>
                    </div>
                ) : (
                    <Row className="g-4">
                        {vendors.map((vendor) => (
                            <Col lg={4} md={6} key={vendor._id}>
                                <Card className="h-100 vendor-card">
                                    <Card.Body>
                                        <div className="d-flex align-items-start justify-content-between mb-3">
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="vendor-avatar">
                                                    {vendor.Business_Name?.charAt(0) || 'V'}
                                                </div>
                                                <div>
                                                    <h6 className="mb-0">{vendor.Business_Name || 'Unknown'}</h6>
                                                    <small className="text-secondary">{vendor.Owner_name}</small>
                                                </div>
                                            </div>
                                            <Badge bg={vendor.Category === 'Technical' ? 'primary' : 'success'}>
                                                {vendor.Category}
                                            </Badge>
                                        </div>

                                        <div className="mb-3">
                                            <div className="d-flex align-items-center mb-2">
                                                <i className="bi bi-envelope me-2 text-primary"></i>
                                                <small>{vendor.Email_address}</small>
                                            </div>
                                            <div className="d-flex align-items-center mb-2">
                                                <i className="bi bi-phone me-2 text-primary"></i>
                                                <small>{vendor.Phone_number}</small>
                                            </div>
                                            <div className="d-flex align-items-center">
                                                <i className="bi bi-geo-alt me-2 text-primary"></i>
                                                <small className="text-truncate">{vendor.Business_address || 'N/A'}</small>
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <small className="text-secondary">Sub-Category:</small>
                                            <div>
                                                {Array.isArray(vendor.Sub_Category)
                                                    ? vendor.Sub_Category.map((sub, i) => (
                                                        <Badge key={i} bg="secondary" className="me-1 mb-1">{sub}</Badge>
                                                    ))
                                                    : <Badge bg="secondary">{vendor.Sub_Category || 'N/A'}</Badge>
                                                }
                                            </div>
                                        </div>

                                        <div className="d-flex gap-2">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="flex-grow-1"
                                                onClick={() => { setSelectedVendor(vendor); setShowModal(true); }}
                                            >
                                                <i className="bi bi-eye me-1"></i> View
                                            </Button>
                                            <Button
                                                variant="success"
                                                size="sm"
                                                className="flex-grow-1"
                                                onClick={() => handleApprove(vendor._id)}
                                                disabled={actionLoading}
                                            >
                                                <i className="bi bi-check-lg me-1"></i> Approve
                                            </Button>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => { setSelectedVendor(vendor); setShowRejectModal(true); }}
                                            >
                                                <i className="bi bi-x-lg"></i>
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}
            </Container>

            {/* Vendor Details Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton style={{ background: 'var(--bg-secondary)' }}>
                    <Modal.Title>
                        <i className="bi bi-person-badge me-2"></i>
                        Vendor Application
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedVendor && (
                        <>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <h6 className="text-secondary">Business Name</h6>
                                    <p className="fw-bold">{selectedVendor.Business_Name}</p>
                                </Col>
                                <Col md={6}>
                                    <h6 className="text-secondary">Owner Name</h6>
                                    <p className="fw-bold">{selectedVendor.Owner_name}</p>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <h6 className="text-secondary">Email</h6>
                                    <p>{selectedVendor.Email_address}</p>
                                </Col>
                                <Col md={6}>
                                    <h6 className="text-secondary">Phone</h6>
                                    <p>{selectedVendor.Phone_number}</p>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <h6 className="text-secondary">Category</h6>
                                    <Badge bg="primary">{selectedVendor.Category}</Badge>
                                </Col>
                                <Col md={6}>
                                    <h6 className="text-secondary">Sub-Category</h6>
                                    <p>{Array.isArray(selectedVendor.Sub_Category)
                                        ? selectedVendor.Sub_Category.join(', ')
                                        : selectedVendor.Sub_Category}
                                    </p>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={12}>
                                    <h6 className="text-secondary">Business Address</h6>
                                    <p>{selectedVendor.Business_address}</p>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <h6 className="text-secondary">Tax ID</h6>
                                    <p>{selectedVendor.Tax_ID || 'N/A'}</p>
                                </Col>
                                <Col md={6}>
                                    <h6 className="text-secondary">Location</h6>
                                    <p>
                                        {selectedVendor.Latitude && selectedVendor.Longitude
                                            ? `${selectedVendor.Latitude}, ${selectedVendor.Longitude}`
                                            : 'Not provided'}
                                    </p>
                                </Col>
                            </Row>
                            {selectedVendor.ProductUrl && (
                                <div className="mt-3">
                                    <h6 className="text-secondary">Document</h6>
                                    <a href={selectedVendor.ProductUrl} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary btn-sm">
                                        <i className="bi bi-file-earmark-pdf me-2"></i>View Document
                                    </a>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
                    <Button variant="danger" onClick={() => { setShowModal(false); setShowRejectModal(true); }}>
                        <i className="bi bi-x-lg me-1"></i> Reject
                    </Button>
                    <Button variant="success" onClick={() => handleApprove(selectedVendor?._id)} disabled={actionLoading}>
                        {actionLoading ? <Spinner size="sm" /> : <><i className="bi bi-check-lg me-1"></i> Approve</>}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Reject Modal */}
            <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered>
                <Modal.Header closeButton style={{ background: 'var(--bg-secondary)' }}>
                    <Modal.Title>Reject Application</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to reject <strong>{selectedVendor?.Business_Name}</strong>?</p>
                    <Form.Group>
                        <Form.Label>Reason for rejection</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Enter reason for rejection..."
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRejectModal(false)}>Cancel</Button>
                    <Button variant="danger" onClick={handleReject} disabled={actionLoading}>
                        {actionLoading ? <Spinner size="sm" /> : 'Reject Application'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default PendingApprovals;
